import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { EmployeeName } from '@/components/EmployeeName';
import { ShieldWarning, ShieldCheck, Warning, Info, Devices, Globe, Clock as ClockIcon } from '@phosphor-icons/react';
import BackButton from '@/components/BackButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const SEV_STYLE = {
  high: { cls: 'bg-red-100 text-red-800 border-red-300', ar: 'عالٍ', en: 'High', icon: ShieldWarning },
  med:  { cls: 'bg-orange-100 text-orange-800 border-orange-300', ar: 'متوسط', en: 'Medium', icon: Warning },
  low:  { cls: 'bg-amber-50 text-amber-700 border-amber-200', ar: 'منخفض', en: 'Low', icon: Info },
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', timeZone: 'Asia/Riyadh' });
  } catch { return iso; }
}

export default function SecurityAudit({ embed = false }) {
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const { isAdmin } = useAuth();
  const isAr = language === 'ar';

  const [findings, setFindings] = useState([]);
  const [summary, setSummary] = useState({ high: 0, med: 0, low: 0 });
  const [records, setRecords] = useState([]);
  const [days, setDays] = useState(7);
  const [auditStart, setAuditStart] = useState('');
  const [auditEnd, setAuditEnd] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchSuspicious = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/security/suspicious-attendance`, {
        params: { days },
        withCredentials: true,
      });
      setFindings(res.data.findings || []);
      setSummary(res.data.summary || { high: 0, med: 0, low: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر تحميل التنبيهات' : 'Load failed'));
    }
  }, [days, isAr]);

  const fetchAudit = useCallback(async () => {
    try {
      const params = {};
      if (auditStart) params.start_date = auditStart;
      if (auditEnd) params.end_date = auditEnd;
      const res = await axios.get(`${API}/security/attendance-audit`, {
        params, withCredentials: true,
      });
      setRecords(res.data.records || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر تحميل السجل' : 'Load failed'));
    }
  }, [auditStart, auditEnd, isAr]);

  useEffect(() => {
    if (!isAdmin) { navigate('/home'); return; }
    setLoading(true);
    Promise.all([fetchSuspicious(), fetchAudit()]).finally(() => setLoading(false));
  }, [isAdmin, navigate, fetchSuspicious, fetchAudit]);

  const renderBody = () => (
    <>
        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-red-700/80">{isAr ? 'تنبيهات عالية' : 'High alerts'}</p>
                <p className="text-3xl font-bold text-red-700" data-testid="stat-high">{summary.high}</p>
              </div>
              <ShieldWarning size={36} weight="duotone" className="text-red-600" />
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-700/80">{isAr ? 'تنبيهات متوسطة' : 'Medium alerts'}</p>
                <p className="text-3xl font-bold text-orange-700" data-testid="stat-med">{summary.med}</p>
              </div>
              <Warning size={36} weight="duotone" className="text-orange-600" />
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-700/80">{isAr ? 'تنبيهات للعلم' : 'Informational'}</p>
                <p className="text-3xl font-bold text-amber-700" data-testid="stat-low">{summary.low}</p>
              </div>
              <Info size={36} weight="duotone" className="text-amber-600" />
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="alerts" className="w-full">
          <div className="flex justify-end">
            <TabsList className="bg-white border border-[#C4BFF0]">
              <TabsTrigger value="alerts" data-testid="tab-alerts">
                <ShieldWarning size={16} className="me-1" />
                {isAr ? 'التنبيهات الذكية' : 'Smart Alerts'}
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">
                <ClockIcon size={16} className="me-1" />
                {isAr ? 'سجل التدقيق الكامل' : 'Full Audit Log'}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ─── Alerts tab ─── */}
          <TabsContent value="alerts" className="mt-3 space-y-3">
            <Card className="border-[#C4BFF0]">
              <CardHeader className="flex flex-row-reverse items-center justify-between gap-2 pb-2">
                <CardTitle className="text-sm font-semibold text-[#655dc6]">
                  {isAr ? 'تنبيهات حضور مشبوهة' : 'Suspicious clock-ins'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-[#655dc6]/70">{isAr ? 'فترة' : 'Window'}:</label>
                  <select
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value, 10))}
                    className="text-xs border border-[#C4BFF0] rounded-md px-2 py-1 bg-white"
                    data-testid="window-select"
                  >
                    <option value={1}>{isAr ? 'اليوم' : 'Today'}</option>
                    <option value={7}>{isAr ? 'آخر 7 أيام' : 'Last 7 days'}</option>
                    <option value={30}>{isAr ? 'آخر 30 يوماً' : 'Last 30 days'}</option>
                    <option value={90}>{isAr ? 'آخر 90 يوماً' : 'Last 90 days'}</option>
                  </select>
                  <Button size="sm" variant="outline" onClick={fetchSuspicious} data-testid="refresh-alerts">
                    {isAr ? 'تحديث' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {findings.length === 0 ? (
                  <div className="py-10 text-center">
                    <ShieldCheck size={48} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
                    <p className="text-sm text-emerald-700 font-semibold">
                      {isAr ? 'لا توجد تنبيهات — جميع البصمات تبدو طبيعية' : 'No alerts — all clock-ins look normal'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {findings.map((f, idx) => {
                      const sev = SEV_STYLE[f.severity] || SEV_STYLE.low;
                      const SevIcon = sev.icon;
                      return (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${sev.cls.replace('text-', 'border-').split(' ').filter(c => c.startsWith('border-')).join(' ')} bg-white`}
                          data-testid={`finding-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <SevIcon size={18} weight="fill" className={sev.cls.split(' ').filter(c => c.startsWith('text-')).join(' ')} />
                              <Badge className={sev.cls}>{isAr ? sev.ar : sev.en}</Badge>
                              <span className="text-sm font-semibold text-[#1a1a1a]">
                                {isAr ? f.title : (f.title_en || f.title)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">{f.date}</span>
                          </div>
                          <div className="text-xs text-gray-700 space-y-0.5 ps-7">
                            {f.rule === 'same_device_multiple_users' && (
                              <>
                                <div className="flex items-center gap-1">
                                  <Globe size={12} /> {isAr ? 'الجهاز:' : 'Device:'} <code className="bg-gray-100 px-1 rounded">{f.device_id?.slice(0, 24) || '-'}</code>
                                  {f.ip_address && <span className="text-gray-400 ms-2">IP: {f.ip_address}</span>}
                                </div>
                                <div>
                                  {isAr ? 'الموظفون:' : 'Employees:'}{' '}
                                  {f.users.map((u, i) => (
                                    <span key={i} className="me-1">
                                      <strong>{u.user_name}</strong> ({fmtTime(u.ts)}){i < f.users.length - 1 ? '،' : ''}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                            {f.rule === 'multi_device_same_day' && (
                              <>
                                <div><strong>{f.user_name}</strong></div>
                                <div className="flex items-center gap-1">
                                  <Devices size={12} /> {isAr ? 'عدد الأجهزة:' : 'Devices:'} <strong>{f.device_count}</strong>
                                </div>
                              </>
                            )}
                            {f.rule === 'new_device' && (
                              <>
                                <div><strong>{f.user_name}</strong></div>
                                <div className="flex items-center gap-1">
                                  <Devices size={12} /> {f.user_agent} · ID: <code className="bg-gray-100 px-1 rounded text-[10px]">{(f.device_id || '').slice(0, 14)}…</code>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Full audit tab ─── */}
          <TabsContent value="audit" className="mt-3">
            <Card className="border-[#C4BFF0]">
              <CardHeader className="flex flex-row-reverse items-center justify-between gap-2 pb-2">
                <CardTitle className="text-sm font-semibold text-[#655dc6]">
                  {isAr ? 'سجل البصمات الكامل (IP / الجهاز / المتصفح)' : 'Full clock-in/out audit (IP / Device / Browser)'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    type="date" value={auditStart} onChange={(e) => setAuditStart(e.target.value)}
                    className="h-8 text-xs w-36" data-testid="audit-start"
                  />
                  <Input
                    type="date" value={auditEnd} onChange={(e) => setAuditEnd(e.target.value)}
                    className="h-8 text-xs w-36" data-testid="audit-end"
                  />
                  <Button size="sm" variant="outline" onClick={fetchAudit} data-testid="apply-audit-filter">
                    {isAr ? 'تطبيق' : 'Apply'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-10 text-center text-gray-500">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
                ) : records.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">
                    {isAr ? 'لا توجد سجلات في هذه الفترة' : 'No records in this period'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F2F0FA]">
                          <TableHead className="text-[#655dc6]">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                          <TableHead className="text-[#655dc6]">{isAr ? 'التاريخ' : 'Date'}</TableHead>
                          <TableHead className="text-[#655dc6]">{isAr ? 'حضور' : 'Clock-in'}</TableHead>
                          <TableHead className="text-[#655dc6]">IP</TableHead>
                          <TableHead className="text-[#655dc6]">{isAr ? 'الجهاز/المتصفح' : 'Device / Browser'}</TableHead>
                          <TableHead className="text-[#655dc6]">{isAr ? 'انصراف' : 'Clock-out'}</TableHead>
                          <TableHead className="text-[#655dc6]">IP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records.map((r, i) => (
                          <TableRow key={i} className="border-[#C4BFF0] text-xs">
                            <TableCell><EmployeeName name={r.user_name} nameEn={r.user_name_en} /></TableCell>
                            <TableCell>{r.date}</TableCell>
                            <TableCell>{fmtTime(r.clock_in)}</TableCell>
                            <TableCell><code className="bg-gray-100 px-1 rounded">{r.clock_in_ip || '—'}</code></TableCell>
                            <TableCell>
                              <div>{r.clock_in_ua_short || '—'}</div>
                              {r.clock_in_device_id && (
                                <div className="text-[10px] text-gray-500" title={r.clock_in_device_id}>
                                  ID: {r.clock_in_device_id.slice(0, 12)}…
                                </div>
                              )}
                            </TableCell>
                            <TableCell>{fmtTime(r.clock_out)}</TableCell>
                            <TableCell><code className="bg-gray-100 px-1 rounded">{r.clock_out_ip || '—'}</code></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </>
  );

  return embed ? (
    <div className="space-y-6">{renderBody()}</div>
  ) : (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="security-audit-page" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6] hover:bg-[#E8E6F5]" testId="back-home-btn" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-3">
            <ShieldWarning size={32} weight="duotone" />
            {isAr ? 'سجل التدقيق الأمني' : 'Security Audit'}
          </h1>
          <p className="text-sm text-[#655dc6]/70 mt-1 ms-1">
            {isAr ? 'كشف الاستخدام المشبوه لبيانات الدخول' : 'Detect suspicious credential usage'}
          </p>
        </div>
        {renderBody()}
      </main>
    </div>
  );
}
